import React, { useRef, useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';


const SignatureCapture = ({ onSignatureChange, initialSignature = null }) => {
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasSignature, setHasSignature] = useState(!!initialSignature);
  const [lastPos, setLastPos] = useState(null);

  useEffect(() => {
    const canvas = canvasRef?.current;
    if (!canvas) return;
    const ctx = canvas?.getContext('2d');
    ctx.strokeStyle = '#1a1a1a';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';

    if (initialSignature) {
      const img = new Image();
      img.onload = () => ctx?.drawImage(img, 0, 0);
      img.src = initialSignature;
    }
  }, []);

  const getPos = (e, canvas) => {
    const rect = canvas?.getBoundingClientRect();
    const scaleX = canvas?.width / rect?.width;
    const scaleY = canvas?.height / rect?.height;
    if (e?.touches) {
      return {
        x: (e?.touches?.[0]?.clientX - rect?.left) * scaleX,
        y: (e?.touches?.[0]?.clientY - rect?.top) * scaleY
      };
    }
    return {
      x: (e?.clientX - rect?.left) * scaleX,
      y: (e?.clientY - rect?.top) * scaleY
    };
  };

  const startDrawing = (e) => {
    e?.preventDefault();
    const canvas = canvasRef?.current;
    if (!canvas) return;
    setIsDrawing(true);
    const pos = getPos(e, canvas);
    setLastPos(pos);
  };

  const draw = (e) => {
    e?.preventDefault();
    if (!isDrawing) return;
    const canvas = canvasRef?.current;
    if (!canvas) return;
    const ctx = canvas?.getContext('2d');
    const pos = getPos(e, canvas);

    ctx?.beginPath();
    ctx?.moveTo(lastPos?.x, lastPos?.y);
    ctx?.lineTo(pos?.x, pos?.y);
    ctx?.stroke();
    setLastPos(pos);
    setHasSignature(true);
  };

  const stopDrawing = (e) => {
    e?.preventDefault();
    if (!isDrawing) return;
    setIsDrawing(false);
    const canvas = canvasRef?.current;
    if (canvas && hasSignature) {
      const dataUrl = canvas?.toDataURL('image/png');
      onSignatureChange?.(dataUrl);
    }
  };

  const clearSignature = () => {
    const canvas = canvasRef?.current;
    if (!canvas) return;
    const ctx = canvas?.getContext('2d');
    ctx?.clearRect(0, 0, canvas?.width, canvas?.height);
    setHasSignature(false);
    onSignatureChange?.(null);
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
            hasSignature ? 'bg-success text-success-foreground' : 'bg-primary/10 text-primary'
          }`}>
            <Icon name="PenLine" size={20} strokeWidth={2} />
          </div>
          <div>
            <h3 className="font-heading text-lg font-semibold text-foreground">
              Digital Signature
            </h3>
            <p className="font-body text-sm text-muted-foreground">
              Sign below to confirm trailer condition documentation
            </p>
          </div>
        </div>
        {hasSignature && (
          <button
            onClick={clearSignature}
            className="flex items-center space-x-1 text-sm text-muted-foreground hover:text-error transition-smooth"
          >
            <Icon name="Trash2" size={14} strokeWidth={2} />
            <span className="font-body text-xs">Clear</span>
          </button>
        )}
      </div>

      <div className={`border-2 rounded-lg overflow-hidden ${
        hasSignature ? 'border-success' : 'border-border'
      }`}>
        <canvas
          ref={canvasRef}
          width={600}
          height={150}
          className="w-full bg-white cursor-crosshair touch-none"
          style={{ height: '150px' }}
          onMouseDown={startDrawing}
          onMouseMove={draw}
          onMouseUp={stopDrawing}
          onMouseLeave={stopDrawing}
          onTouchStart={startDrawing}
          onTouchMove={draw}
          onTouchEnd={stopDrawing}
        />
      </div>

      {!hasSignature && (
        <p className="font-caption text-xs text-muted-foreground text-center">
          Draw your signature in the box above
        </p>
      )}

      {hasSignature && (
        <div className="flex items-center space-x-2 text-success">
          <Icon name="CheckCircle2" size={16} strokeWidth={2} />
          <p className="font-body text-sm font-medium">Signature captured</p>
        </div>
      )}
    </div>
  );
};

export default SignatureCapture;
